import { QueryClient } from "@tanstack/react-query";
import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import { createRoot, hydrateRoot } from "react-dom/client";
import Components from "./components";

//prevent crash when vite dyanmic import fails because of the outdated cache.
window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

declare global {
  interface Window {
    React: typeof React;
    ReactDOM: typeof ReactDOM;
    ReactQuery: typeof QueryClient;
    ReactComponents: Record<string, React.ComponentType<any>>;
  }
}

window.React = React;
window.ReactDOM = ReactDOM;
window.ReactQuery = QueryClient;

window.ReactComponents = Components;

function mountDataComponents() {
  const containers = document.querySelectorAll<HTMLElement>("[data-component]");
  for (const container of containers) {
    const nameAttr = container.dataset.component;
    if (!nameAttr) {
      console.warn("Missing data-component attribute on container", container);
      continue;
    }
    const props = JSON.parse(container.dataset.props || "{}");
    const components = window.ReactComponents as Record<string, any>;
    const hasExact = nameAttr in components;
    const matchedKey = hasExact
      ? nameAttr
      : Object.keys(components).find((key) => key.toLowerCase() === nameAttr.toLowerCase());
    const Component = matchedKey ? components[matchedKey] : undefined;
    if (!Component) {
      console.warn(`React component "${nameAttr}" not found`);
      continue;
    }
    const root = createRoot(container);
    root.render(
      React.createElement(
        Suspense,
        { fallback: React.createElement("div", { style: {} }, "") },
        React.createElement(Component, props)
      )
    );
  }
}

const EXT_REGEX = /\.(tsx|ts|jsx|js)$/i;

function hydrateSSRComponents() {
  const ssrContainers = document.querySelectorAll<HTMLElement>("[data-ssr]");
  const loaders = import.meta.glob("../ssr/components/*.{tsx,ts,jsx,js}");

  for (const container of ssrContainers) {
    const nameAttr = container.dataset.ssr;
    const propsJson = container.dataset.ssrProps || "{}";
    if (!nameAttr) {
      console.warn("Missing data-ssr attribute on container", container);
      continue;
    }
    let props: any = {};
    try {
      props = JSON.parse(propsJson);
    } catch (e) {
      console.warn("Invalid JSON in data-ssr-props for", nameAttr, e);
    }

    const matchingPath = Object.keys(loaders).find((p) => {
      const base = p.split("/").pop()!.replace(EXT_REGEX, "");
      return base === nameAttr || base.toLowerCase() === nameAttr.toLowerCase();
    });

    if (!matchingPath) {
      console.warn(`SSR Component "${nameAttr}" not found in ../ssr/components`);
      continue;
    }

    const load = loaders[matchingPath] as () => Promise<any>;
    load()
      .then((mod) => {
        const Component = (mod as any).default || (mod as any)[nameAttr!];
        if (!Component) {
          console.warn(`SSR Component module did not export ${nameAttr}`);
          return;
        }
        hydrateRoot(container, React.createElement(Component, props));
      })
      .catch((err) => {
        console.error(`Failed to hydrate SSR component ${nameAttr}`, err);
      });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  mountDataComponents();
  hydrateSSRComponents();
});
